import { useLocation, useNavigate } from 'react-router-dom'
import AnimationButton from '../AnimationButton/AnimationButton'
import colors from '../../theme/color'
import {
  getTechnoDetailNavigationPath,
  isTechnoDetailServiceActiveForPath,
} from '../../../app/utils/constants'
import { useEffect, useRef, useState } from 'react'

export type TechnoDetailsProps = {
  serviceNames: string[]
  imageSrc: string
  imageAlt: string
  titleBefore: string
  titleGradient: string
  titleAfter: string
  description: string
}

function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current

    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('in-view')
        else el.classList.remove('in-view')
      },
      { threshold: 0.05 },
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return ref
}

type ButtonRowProps = {
  services: string[]
  rowIndex: number
  pathname: string
  navigate: (path: string) => void
}

function ButtonRow({
  services,
  rowIndex,
  pathname,
  navigate,
}: ButtonRowProps) {
  const rowRef = useRef<HTMLDivElement>(null)

  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = rowRef.current

    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 },
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const isEven = services.length % 2 === 0

  const mid = Math.ceil(services.length / 2)

  const getTransform = (idx: number): string => {
    if (!isEven) return 'translateY(70px)'

    return idx < mid
      ? 'translateX(-120px)'
      : 'translateX(120px)'
  }

  return (
    <div
      ref={rowRef}
      className="flex w-full flex-wrap justify-center gap-3 md:gap-5"
    >
      {services.map((service, idx) => {
        const delay = isEven
          ? `${Math.abs(idx - mid + (services.length % 2 === 0 ? 0.5 : 0)) * 0.07}s`
          : `${idx * 0.09}s`

        return (
          <div
            key={service}
            style={{
              transform: inView ? 'none' : getTransform(idx),
              opacity: inView ? 1 : 0,
              transition: `transform 0.65s ease-in-out ${delay}, opacity 0.5s ease-in ${delay}`,
              willChange: 'transform, opacity',
            }}
            className="h-14 w-[calc(50%-0.375rem)] rounded-full bg-[linear-gradient(168.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)] p-px sm:w-[calc(50%-0.5rem)] md:h-20 md:w-[calc(25%-0.9375rem)] lg:h-16 lg:w-[calc(25%-0.9375rem)] xl:h-15 2xl:h-18"
          >
            <AnimationButton
              onClick={() => {
                const path =
                  getTechnoDetailNavigationPath(service)

                if (path) navigate(path)
              }}
              style={
                isTechnoDetailServiceActiveForPath(
                  service,
                  pathname,
                )
                  ? { backgroundColor: colors.purpleHover }
                  : undefined
              }
              className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-[#150d3fee] px-2 py-2 text-center font-rajdhani text-[13px] font-semibold leading-tight whitespace-normal break-words transition-colors duration-300 sm:px-4 sm:text-sm md:px-6 md:py-3 md:text-base lg:px-4 lg:text-[18px] xl:text-[18px] 2xl:text-[25px]"
            >
              {service}
            </AnimationButton>
          </div>
        )
      })}
    </div>
  )
}

function chunkIntoRows(
  services: string[],
  perRow = 4,
): string[][] {
  const rows: string[][] = []

  for (let i = 0; i < services.length; i += perRow) {
    rows.push(services.slice(i, i + perRow))
  }

  return rows
}

const TechnoDetails = ({
  serviceNames,
  imageSrc,
  imageAlt,
  titleBefore,
  titleGradient,
  titleAfter,
  description,
}: TechnoDetailsProps) => {
  const navigate = useNavigate()

  const { pathname } = useLocation()

  const imageRef = useScrollReveal<HTMLImageElement>()

  const paraRef = useScrollReveal<HTMLParagraphElement>()

  const activeIndex = serviceNames.findIndex((service) =>
    isTechnoDetailServiceActiveForPath(service, pathname),
  )

  const orderedServiceNames =
    activeIndex >= 0
      ? [
          ...serviceNames.slice(activeIndex),
          ...serviceNames.slice(0, activeIndex),
        ]
      : serviceNames

  const rows = chunkIntoRows(orderedServiceNames, 4)

  return (
    <>
      <style>{`
        .techno-img {
          transform: scale(0.75);
          opacity: 0;
          transition: transform 0.75s ease-in, opacity 0.6s ease;
          will-change: transform, opacity;
        }

        .techno-img.in-view {
          transform: scale(1);
          opacity: 1;
        }

        .techno-para-wrap {
          display: block;
          width: 100%;
        }

        .techno-para {
          display: block;
          transform: translateY(100px);
          opacity: 0;
          transition: transform 0.85s ease-in 0.1s, opacity 0.55s ease 0.1s;
          will-change: transform, opacity;
        }

        .techno-para.in-view {
          transform: translateY(0);
          opacity: 1;
        }

        @keyframes zoomPulse {
          0%, 100% {
            transform: scale(1);
          }

          50% {
            transform: scale(1.05);
          }
        }

        .gradient-pulse {
          display: inline-block;
          animation: zoomPulse 2.2s ease-in-out infinite;
        }
      `}</style>

      <section className="mx-3 md:mx-6 xl:mx-[10%]">

        {/* BUTTONS */}
        <div className="flex flex-col gap-3 md:gap-5">
          {rows.map((rowServices, rowIdx) => (
            <ButtonRow
              key={rowIdx}
              services={rowServices}
              rowIndex={rowIdx}
              pathname={pathname}
              navigate={navigate}
            />
          ))}
        </div>

        {/* CONTENT */}
        <div className="mx-auto mt-8 flex flex-col items-center text-center md:mt-14">

          <div
            className="w-full rounded-3xl p-3 md:w-auto md:p-5"
            style={{
              backgroundColor: 'rgba(25, 16, 62, 1)',
            }}
          >
            <img
              ref={imageRef}
              src={imageSrc}
              alt={imageAlt}
              className="techno-img h-auto max-h-[260px] w-full rounded-2xl object-contain sm:max-h-[350px] md:h-[50vh] md:w-[95vh]"
            />
          </div>

          <span className="techno-para-wrap mt-4">
            <p
              ref={paraRef}
              className="techno-para px-2 font-gilroy-medium text-sm leading-relaxed text-gray-400 md:text-base lg:text-lg xl:text-xl 2xl:text-3xl"
            >
              {description}
            </p>
          </span>

        </div>
      </section>
    </>
  )
}

export default TechnoDetails